import { useTranslation } from 'react-i18next';
import { VStack, Text, Box, Badge, Field } from '@chakra-ui/react';
import { InputField } from '@themes/aegix';
import { Controller, useFormContext } from 'react-hook-form';
import { SelectInput } from '@themes/aegix';
import { useOrganizations } from '../../hooks/queries';
import RolePermissionPicker from './RolePermissionPicker';
import { RoleFormValues } from '../../types/role';

interface RoleFormProps {
  isSystemRole?: boolean;
}

const RoleForm: React.FC<RoleFormProps> = ({ isSystemRole = false }) => {
  const { t } = useTranslation();
  const {
    register,
    setValue,
    control,
    watch,
    formState: { errors, touchedFields, submitCount },
  } = useFormContext<RoleFormValues>();
  const { data: orgOptions = [], isError: orgError } = useOrganizations();

  const organizationOptions = orgOptions.map((o) => ({
    label: o.label,
    value: String(o.id),
  }));

  // After the first submit attempt, surface every field error regardless of
  // whether the user has touched the field — otherwise a user who clicks Save
  // on a freshly-opened drawer sees no feedback.
  const showError = (
    field: 'name' | 'organization_id' | 'permissions',
  ): boolean =>
    !!errors?.[field] && (touchedFields?.[field] === true || submitCount > 0);

  return (
    <VStack gap={4} align="stretch">
      {isSystemRole && (
        <Box
          p={3}
          bg="blue.50"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
        >
          <Badge colorScheme="blue" mb={2}>
            {t('roles.systemRoleBadge', 'System Role')}
          </Badge>
          <Text fontSize="sm" color="blue.700">
            {t(
              'roles.systemRoleNotice',
              'System roles are managed by the platform and cannot be modified. You can clone this role to create a custom version.',
            )}
          </Text>
        </Box>
      )}

      <Field.Root invalid={showError('name')} required={!isSystemRole}>
        <Field.Label htmlFor="role-name">
          {t('roles.roleName', 'Role Name')}
          {!isSystemRole && <Field.RequiredIndicator />}
        </Field.Label>
        <InputField
          id="role-name"
          value={watch('name') || ''}
          placeholder={t('roles.enterRoleName', 'Enter role name')}
          autoComplete="off"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- role form opens in drawer; focus primary field unless it's a read-only system role
          autoFocus={!isSystemRole}
          disabled={isSystemRole}
          data-e2e="role-name-input"
          {...register('name')}
          aria-describedby={showError('name') ? 'role-name-error' : undefined}
        />
        {showError('name') && (
          <Field.ErrorText id="role-name-error" data-e2e="role-name-error">
            {errors.name?.message}
          </Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root>
        <Field.Label htmlFor="role-description">{t('roles.description', 'Description')}</Field.Label>
        <InputField
          id="role-description"
          value={watch('description') || ''}
          placeholder={t('roles.enterRoleDesc', 'Enter role description (optional)')}
          autoComplete="off"
          disabled={isSystemRole}
          data-e2e="role-description-input"
          {...register('description')}
        />
      </Field.Root>

      {!isSystemRole && (
        <VStack gap={1.5} align="stretch">
          <SelectInput
            id="role-organization"
            name="role-organization"
            value={watch('organization_id') || ''}
            label={t('roles.orgRequired', 'Organization *')}
            option={organizationOptions}
            onChange={(value) =>
              setValue('organization_id', value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              })
            }
            invalid={showError('organization_id')}
            error={showError('organization_id') ? errors.organization_id?.message : undefined}
            e2eTitle="role-organization-select"
          />
          {orgError && (
            <Text fontSize="xs" color="text.danger">
              {t('roles.failedLoadOrgs', 'Failed to load organizations. Please try refreshing.')}
            </Text>
          )}
        </VStack>
      )}

      {/*
        NOTE: do NOT wrap this section in <Field.Root>. Chakra v3's checkbox
        machine binds Checkbox.HiddenInput to the surrounding Field's id when
        one is present, so multiple Checkbox.Roots inside the same Field.Root
        all share the same DOM id — and any label click (the Field.Label or a
        Checkbox.Label inside any card) routes to the *first* hidden input,
        making it look like every click toggles the top category. Render the
        label and error text manually instead.
      */}
      {/* Reset the browser's default <fieldset> margin/border/padding so the
          semantic grouping doesn't introduce a stray inset box (ASM-299). */}
      <VStack as="fieldset" gap={1.5} align="stretch" border="none" m={0} p={0}>
        <Text as="legend" fontSize="sm" fontWeight="medium" color="text.primary">
          {t('roles.permissions', 'Permissions')}
          {!isSystemRole && (
            <Text as="span" color="text.danger" ml={1}>
              *
            </Text>
          )}
        </Text>
        <Controller
          name="permissions"
          control={control}
          render={({ field }) => (
            <RolePermissionPicker
              selectedPermissions={field.value || []}
              onChange={(ids) => field.onChange(ids)}
              disabled={isSystemRole}
            />
          )}
        />
        {showError('permissions') && (
          <Text fontSize="xs" color="text.danger">
            {errors.permissions?.message}
          </Text>
        )}
      </VStack>
    </VStack>
  );
};

export default RoleForm;
